import { HttpErrorResponse } from "@angular/common/http";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import pl from "i18n-iso-countries/langs/pl.json";
import es from "i18n-iso-countries/langs/es.json";
import type { Branch } from "../branch-settings/branch-settings.interface";
import type { EmployeePickerItem } from "../employees/employees.interface";
import type { Employee } from "../employees/employees.interface";
import {
  PRINT_CARD_MULTILOFT_QUANTITY_OPTIONS,
  PRINT_CARD_PAPER_QUANTITY_OPTIONS,
  PRINT_CARD_PLASTIC_QUANTITY_MAX,
  PRINT_CARD_PLASTIC_QUANTITY_MIN,
  type PrintCardTemplateType,
} from "../print-cards/print-cards.interface";
import {
  FULFILLMENT_BLOCKED_REASON,
  type BranchShippingDetails,
  type CardOrderCartEmployeeGroup,
  type CardOrderCartLine,
  type CardOrderDeliverySpeed,
  type CardOrderDeliveryType,
  type CardOrderProductionTime,
  type CardOrderFulfillmentTimelineStep,
  type CardOrderShipmentCarrier,
  type CardOrderOptions,
  type CardOrderTestShippingAddress,
  type CardOrderUpsPackageDetails,
  type CardOrderUpsTrackingActivity,
  type CardOrderUpsTrackingDetails,
  type DeliverableBranch,
  type OrderItemsGroupView,
  type OrderItemsListRecipient,
  type OrderItemsSummary,
  type PlacingUserContactSource,
} from "./card-orders.interface";

countries.registerLocale(en);
countries.registerLocale(pl);
countries.registerLocale(es);

export function getHttpErrorMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }

  const body: unknown = error.error;
  if (typeof body === "string" && body.trim()) {
    return body.trim();
  }

  if (body && typeof body === "object") {
    const message = (body as { error?: unknown }).error;
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }

  return null;
}

const UPS_COUNTRY_ALIASES: Record<string, string> = {
  POLAND: "PL",
  POLSKA: "PL",
  JPN: "JP",
  JAPAN: "JP",
  DEU: "DE",
  GERMANY: "DE",
  DEUTSCHLAND: "DE",
  FRA: "FR",
  FRANCE: "FR",
  GBR: "GB",
  UK: "GB",
  UNITEDKINGDOM: "GB",
  GREATBRITAIN: "GB",
  ENGLAND: "GB",
  SCOTLAND: "GB",
  WALES: "GB",
  USA: "US",
  UNITEDSTATES: "US",
  UNITEDSTATESOFAMERICA: "US",
  ESP: "ES",
  SPAIN: "ES",
  ESPANA: "ES",
  ITA: "IT",
  ITALY: "IT",
  ITALIA: "IT",
  NLD: "NL",
  NETHERLANDS: "NL",
  HOLLAND: "NL",
  BEL: "BE",
  BELGIUM: "BE",
  BELGIE: "BE",
  AUT: "AT",
  AUSTRIA: "AT",
  OSTERREICH: "AT",
  CHE: "CH",
  SWITZERLAND: "CH",
  SCHWEIZ: "CH",
  SWE: "SE",
  SWEDEN: "SE",
  SVERIGE: "SE",
  NOR: "NO",
  NORWAY: "NO",
  NORGE: "NO",
  DNK: "DK",
  DENMARK: "DK",
  DANMARK: "DK",
  FIN: "FI",
  FINLAND: "FI",
  SUOMI: "FI",
  IRL: "IE",
  IRELAND: "IE",
  PRT: "PT",
  PORTUGAL: "PT",
  CZE: "CZ",
  CZECHIA: "CZ",
  CZECHREPUBLIC: "CZ",
  CESKAREPUBLIKA: "CZ",
  SVK: "SK",
  SLOVAKIA: "SK",
  SLOVENSKO: "SK",
  HUN: "HU",
  HUNGARY: "HU",
  MAGYARORSZAG: "HU",
  ROU: "RO",
  ROMANIA: "RO",
  CAN: "CA",
  CANADA: "CA",
  AUS: "AU",
  AUSTRALIA: "AU",
  CHN: "CN",
  CHINA: "CN",
  KOR: "KR",
  KOREA: "KR",
  SOUTHKOREA: "KR",
  REPUBLICOFKOREA: "KR",
  MEX: "MX",
  MEXICO: "MX",
  BRA: "BR",
  BRAZIL: "BR",
  BRASIL: "BR",
  IND: "IN",
  INDIA: "IN",
  LUX: "LU",
  LUXEMBOURG: "LU",
  HRV: "HR",
  CROATIA: "HR",
  SVN: "SI",
  SLOVENIA: "SI",
  GRC: "GR",
  GREECE: "GR",
  UKR: "UA",
  UKRAINE: "UA",
  LTU: "LT",
  LITHUANIA: "LT",
  LVA: "LV",
  LATVIA: "LV",
  EST: "EE",
  ESTONIA: "EE",
  BGR: "BG",
  BULGARIA: "BG",
  SRB: "RS",
  SERBIA: "RS",
  TUR: "TR",
  TURKEY: "TR",
  TURKIYE: "TR",
};

export function upsCountryCode(country: string): string {
  const trimmed = country.trim().toUpperCase();
  const compact = trimmed.replace(/[^A-Z]/g, "");
  if (UPS_COUNTRY_ALIASES[trimmed]) {
    return UPS_COUNTRY_ALIASES[trimmed];
  }
  if (UPS_COUNTRY_ALIASES[compact]) {
    return UPS_COUNTRY_ALIASES[compact];
  }
  if (trimmed.length === 2) {
    return trimmed;
  }
  return "PL";
}

const UPS_NOTIFICATION_LANGUAGE_BY_COUNTRY: Record<string, string> = {
  PL: "POL",
  DE: "DEU",
  FR: "FRA",
  ES: "SPA",
  IT: "ITA",
  NL: "NLD",
  CZ: "CES",
  SK: "SLK",
  HU: "HUN",
  RO: "RON",
  SE: "SWE",
  NO: "NOR",
  DK: "DAN",
  FI: "FIN",
  PT: "POR",
  JP: "JPN",
  US: "ENG",
  GB: "ENG",
  IE: "ENG",
  CA: "ENG",
  AU: "ENG",
  MX: "SPA",
  BR: "POR",
  AR: "SPA",
  CL: "SPA",
  CO: "SPA",
  BE: "NLD",
  AT: "DEU",
  CH: "DEU",
  LU: "FRA",
  TR: "ENG",
};

export function upsNotificationLocale(country: string): {
  language: string;
  dialect: string;
} {
  const dialect = upsCountryCode(country);
  return {
    language: UPS_NOTIFICATION_LANGUAGE_BY_COUNTRY[dialect] ?? "ENG",
    dialect,
  };
}

function sanitizeUpsLocationText(value: string | null | undefined): string {
  if (!value) {
    return "";
  }

  return value
    .replace(/(?:&#x0*D;?|&#x0*A;?|&#13;|&#10;|\r\n|\r|\n)+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function formatUpsAccessPointName(
  value: string | null | undefined,
): string {
  return sanitizeUpsLocationText(value);
}

export function formatUpsAccessPointAddress(point: {
  line1: string;
  line2?: string | null;
  postalCode: string;
  city: string;
}): string {
  return [
    sanitizeUpsLocationText(point.line1),
    point.line2 ? sanitizeUpsLocationText(point.line2) : "",
    `${sanitizeUpsLocationText(point.postalCode)} ${sanitizeUpsLocationText(point.city)}`,
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatUpsAccessPointDistanceKm(
  distanceKm?: number | null,
): number | null {
  if (
    distanceKm === undefined ||
    distanceKm === null ||
    !Number.isFinite(distanceKm)
  ) {
    return null;
  }

  return Math.round(distanceKm * 10) / 10;
}

export function formatUpsAccessPointOpeningHours(
  value: string | null | undefined,
  translate: (key: string) => string,
): string | null {
  const trimmed = sanitizeUpsLocationText(value ?? "");
  if (!trimmed) {
    return null;
  }

  const replacements: Array<[string, string]> = [
    ["Mon-Fri", "cardOrders.accessPointOpeningHours.monFri"],
    ["Mon-Sat", "cardOrders.accessPointOpeningHours.monSat"],
    ["Mon-Thu", "cardOrders.accessPointOpeningHours.monThu"],
    ["Tue-Sat", "cardOrders.accessPointOpeningHours.tueSat"],
    ["Sun", "cardOrders.accessPointOpeningHours.sun"],
    ["Sat", "cardOrders.accessPointOpeningHours.sat"],
    ["Fri", "cardOrders.accessPointOpeningHours.fri"],
    ["Thu", "cardOrders.accessPointOpeningHours.thu"],
    ["Wed", "cardOrders.accessPointOpeningHours.wed"],
    ["Tue", "cardOrders.accessPointOpeningHours.tue"],
    ["Mon", "cardOrders.accessPointOpeningHours.mon"],
    ["Closed", "cardOrders.accessPointOpeningHours.closed"],
    ["No Pickup", "cardOrders.accessPointOpeningHours.noPickup"],
  ];

  return replacements.reduce((result, [source, key]) => {
    const localized = translate(key);
    return localized ? result.split(source).join(localized) : result;
  }, trimmed);
}

export function getCountryDisplayName(
  alpha2Code: string | null | undefined,
  locale = "en",
): string {
  const code = String(alpha2Code ?? "")
    .trim()
    .toUpperCase();
  if (!code) {
    return "";
  }

  const language = locale.split("-")[0].toLowerCase();
  const resolvedLocale =
    language === "pl" || language === "es" ? language : "en";

  return (
    countries.getName(code, resolvedLocale) ??
    countries.getName(code, "en") ??
    code
  );
}

export function getPlacingUserContactDefaults(user: PlacingUserContactSource): {
  recipientName: string;
  phone: string;
} {
  const dynamicVariables = user.dynamicVariables ?? {};
  const recipientName =
    user.fullName?.trim() ||
    user.name?.trim() ||
    [user.givenName, user.familyName]
      .map((value) => value?.trim())
      .filter((value): value is string => !!value)
      .join(" ");
  const phoneFromDynamic = dynamicVariables.phoneNumber;
  const phoneFromUser = user.phoneNumber;
  const phoneNumber = (
    typeof phoneFromDynamic === "string"
      ? phoneFromDynamic
      : typeof phoneFromUser === "string"
        ? phoneFromUser
        : ""
  ).trim();
  const mobileFromDynamic = dynamicVariables.mobileNumber;
  const mobileNumber = (
    typeof mobileFromDynamic === "string" ? mobileFromDynamic : ""
  ).trim();

  return {
    recipientName,
    phone: phoneNumber || mobileNumber,
  };
}

export const DEFAULT_CARD_ORDER_QUANTITY = 250;

const PRINTER_TEMPLATE_TYPES = new Set<PrintCardTemplateType>([
  "PAPER",
  "PAPER_NFC",
  "PLASTIC_NFC",
]);

export function isPrinterTemplateType(type: PrintCardTemplateType): boolean {
  return PRINTER_TEMPLATE_TYPES.has(type);
}

export function getOrderQuantityOptionsForTemplate(
  type: PrintCardTemplateType | null | undefined,
  printerEnabled: boolean,
  quantitiesByTemplateType?: Partial<
    Record<"PAPER" | "PAPER_NFC" | "PLASTIC_NFC", number[]>
  > | null,
  allowedQuantities?: number[] | null,
): number[] {
  if (type === "PLASTIC_NFC") {
    if (!allowedQuantities?.length) {
      return [];
    }
    return [...new Set(allowedQuantities.map(Number))]
      .filter(
        (quantity) =>
          Number.isInteger(quantity) &&
          quantity >= PRINT_CARD_PLASTIC_QUANTITY_MIN &&
          quantity <= PRINT_CARD_PLASTIC_QUANTITY_MAX,
      )
      .sort((a, b) => a - b);
  }

  let options: number[] =
    type === "PAPER_NFC"
      ? [...PRINT_CARD_MULTILOFT_QUANTITY_OPTIONS]
      : [...PRINT_CARD_PAPER_QUANTITY_OPTIONS];
  if (printerEnabled && type) {
    const fromApi = quantitiesByTemplateType?.[type];
    if (fromApi && fromApi.length > 0) {
      options = [...fromApi];
    }
  }
  if (!allowedQuantities?.length) {
    return options;
  }
  return options.filter((quantity) => allowedQuantities.includes(quantity));
}

export function getCardOrderShipmentMethods(
  options: CardOrderOptions | null | undefined,
): CardOrderDeliveryType[] {
  const upsEnabled = options?.upsEnabled ?? false;
  const types: CardOrderDeliveryType[] = options?.shipmentMethods?.length
    ? options.shipmentMethods.map((method) => method.deliveryType)
    : ["COURIER", ...(upsEnabled ? (["ACCESS_POINT"] as const) : []), "PICKUP"];

  return [...new Set(types)].filter(
    (type) => type !== "ACCESS_POINT" || upsEnabled,
  );
}

const SHIPMENT_METHOD_LABEL_KEYS: Record<CardOrderDeliveryType, string> = {
  COURIER: "printCardOrders.deliveryTypeCourier",
  ACCESS_POINT: "printCardOrders.deliveryTypeAccessPoint",
  PICKUP: "printCardOrders.deliveryTypePickup",
};

export function getShipmentMethodLabelKey(type: CardOrderDeliveryType): string {
  return SHIPMENT_METHOD_LABEL_KEYS[type];
}

export function getFulfillmentStageLabelKey(stage: string): string {
  const key = stage === "UPS_SHIPPED" ? "SHIPPED" : stage;
  return `printCardOrders.fulfillment.stage.${key}`;
}

const DELIVERY_SPEED_LABEL_KEYS: Record<CardOrderDeliverySpeed, string> = {
  STANDARD: "printCardOrders.deliverySpeedStandard",
  EXPRESS: "printCardOrders.deliverySpeedExpress",
};

export function getDeliverySpeedLabelKey(
  speed: CardOrderDeliverySpeed,
): string {
  return DELIVERY_SPEED_LABEL_KEYS[speed];
}

const PRODUCTION_TIME_LABEL_KEYS: Record<CardOrderProductionTime, string> = {
  ECONOMY: "printCardOrders.productionTimeEconomy",
  STANDARD: "printCardOrders.productionTimeStandard",
  EXPRESS: "printCardOrders.productionTimeExpress",
};

export function getProductionTimeLabelKey(
  productionTime: CardOrderProductionTime,
): string {
  return PRODUCTION_TIME_LABEL_KEYS[productionTime];
}

const SHIPMENT_CARRIER_LABEL_KEYS: Record<CardOrderShipmentCarrier, string> = {
  UPS: "printCardOrders.shipmentCarrierUps",
  OTHER: "printCardOrders.shipmentCarrierOther",
};

export function getShipmentCarrierLabelKey(
  carrier: CardOrderShipmentCarrier,
): string {
  return SHIPMENT_CARRIER_LABEL_KEYS[carrier];
}

export function getOrderTrackingNumber(order: {
  ups?: { trackingNumber?: string | null } | null;
  externalShipment?: { trackingNumber?: string | null } | null;
}): string | null {
  return (
    order.ups?.trackingNumber?.trim() ||
    order.externalShipment?.trackingNumber?.trim() ||
    null
  );
}

export function getOrderUpsPackages<
  T extends CardOrderUpsPackageDetails = CardOrderUpsPackageDetails,
>(order: { ups?: { packages?: T[] } | null } | null): T[] {
  return order?.ups?.packages ?? [];
}

export function getBlockedPrinterOrderNumbers(
  recipients: Array<{
    fulfillmentBlockedReason?: string | null;
    items: Array<{
      printerOrderNumber?: string | null;
      externalOrderId?: string | null;
      printer?: { orderNumber?: string | null } | null;
    }>;
  }>,
): Set<string> {
  const numbers = new Set<string>();
  for (const recipient of recipients) {
    if (
      recipient.fulfillmentBlockedReason !==
      FULFILLMENT_BLOCKED_REASON.EMPLOYEE_DELETED
    ) {
      continue;
    }
    for (const item of recipient.items) {
      const number =
        item.printerOrderNumber?.trim() ||
        item.printer?.orderNumber?.trim() ||
        item.externalOrderId?.trim() ||
        "";
      if (number) {
        numbers.add(number);
      }
    }
  }
  return numbers;
}

export function hasOrderUpsTracking(
  order: {
    ups?: {
      trackingNumber?: string | null;
      packages?: CardOrderUpsPackageDetails[];
    } | null;
    externalShipment?: {
      trackingNumber?: string | null;
    } | null;
  } | null,
): boolean {
  if (!order) {
    return false;
  }
  if (getOrderTrackingNumber(order)) {
    return true;
  }
  return getOrderUpsPackages(order).some((pkg) => pkg.trackingNumber?.trim());
}

export function hasOrderExternalShipment(
  order: {
    externalShipment?: {
      trackingNumber?: string | null;
      carrierName?: string | null;
    } | null;
  } | null,
): boolean {
  return Boolean(
    order?.externalShipment?.trackingNumber?.trim() &&
    order.externalShipment.carrierName?.trim(),
  );
}

export function getOrderUpsShipmentDescription(
  order: {
    printCardOrderId?: string;
    ups?: { packing?: { shipmentDescription?: string | null } | null } | null;
  } | null,
): string | null {
  const fromPacking = order?.ups?.packing?.shipmentDescription?.trim();
  if (fromPacking) {
    return fromPacking;
  }
  const orderId = (order?.printCardOrderId || "").trim();
  if (orderId) {
    return `DBC1 print cards ${orderId}`;
  }
  return null;
}

export function createUpsPackageDescription(
  packerName: string | null | undefined,
): string {
  const initials = extractPackerInitials(packerName);
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, "0");
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
    now.getDate(),
  )}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return initials ? `${initials}-${stamp}` : stamp;
}

function extractPackerInitials(packerName: string | null | undefined): string {
  const parts = (packerName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function getTemplateTypeLabelKey(templateType: string): string {
  return `printCards.templateType${templateType}`;
}

export function getUpsShippingLabelDataUrl(base64: string): string {
  const trimmed = base64.trim();
  if (trimmed.startsWith("data:image/")) {
    return trimmed;
  }
  return `data:image/gif;base64,${trimmed}`;
}

function printLabelImages(
  pages: Array<{
    dataUrl: string;
    title?: string;
    widthMm?: number;
    heightMm?: number;
  }>,
): void {
  const prepared = pages.filter((page) => page.dataUrl.trim().length > 0);
  if (prepared.length === 0) {
    return;
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;inset:0;width:0;height:0;border:0;visibility:hidden;pointer-events:none;";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }

  const title =
    prepared.length === 1
      ? prepared[0].title?.trim() || "Label"
      : `Labels (${prepared.length})`;
  const sameSize =
    prepared.length > 0 &&
    prepared.every(
      (page) =>
        page.widthMm === prepared[0].widthMm &&
        page.heightMm === prepared[0].heightMm &&
        Boolean(page.widthMm && page.heightMm),
    );
  const pageCss = sameSize
    ? `@page{size:${prepared[0].widthMm}mm ${prepared[0].heightMm}mm;margin:0}`
    : "";
  const imagesHtml = prepared
    .map((page, index) => {
      const alt = (page.title || `Label ${index + 1}`).replace(/[<>&"]/g, "");
      const sizeStyle =
        page.widthMm && page.heightMm
          ? `width:${page.widthMm}mm;height:${page.heightMm}mm;`
          : "max-width:100%;height:auto;";
      return `<img src="${page.dataUrl}" alt="${alt}" style="${sizeStyle}display:block;page-break-after:always;page-break-inside:avoid" />`;
    })
    .join("");

  doc.open();
  doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${title.replace(/[<>&"]/g, "")}</title><style>
html,body{margin:0;padding:0;background:#fff}
body{display:flex;flex-direction:column;align-items:center}
img:last-child{page-break-after:auto}
${pageCss}
@media print{html,body{margin:0;padding:0}}
</style></head><body>${imagesHtml}</body></html>`);
  doc.close();

  const cleanup = () => {
    iframe.remove();
  };
  win.addEventListener("afterprint", cleanup, { once: true });

  const images = Array.from(doc.images);
  let pending = images.length;
  const runPrint = () => {
    win.focus();
    win.print();
    window.setTimeout(cleanup, 60_000);
  };

  if (pending === 0) {
    runPrint();
    return;
  }

  for (const image of images) {
    const onDone = () => {
      pending -= 1;
      if (pending <= 0) {
        runPrint();
      }
    };
    if (image.complete) {
      onDone();
    } else {
      image.addEventListener("load", onDone, { once: true });
      image.addEventListener("error", onDone, { once: true });
    }
  }
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image failed to load"));
    image.src = src;
  });
}

function canvasToPngDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL("image/png");
}

export function printUpsShippingLabels(
  labels: Array<{
    shippingLabelGifBase64: string;
    trackingNumber?: string | null;
  }>,
): void {
  const pages: Array<{ dataUrl: string; title?: string }> = [];
  for (const label of labels) {
    const dataUrl = getUpsShippingLabelDataUrl(
      label.shippingLabelGifBase64.trim(),
    );
    if (!dataUrl) {
      continue;
    }
    pages.push({
      dataUrl,
      title: label.trackingNumber?.trim() || "UPS label",
    });
  }
  printLabelImages(pages);
}

export async function printEmployeeNameLabel(options: {
  givenName: string;
  familyName: string;
  qrDataUrl?: string | null;
}): Promise<void> {
  const givenName = options.givenName.trim();
  const familyName = options.familyName.trim();
  const personName = [givenName, familyName].filter(Boolean).join(" ").trim();
  const qrDataUrl = options.qrDataUrl?.trim() || "";
  if (!personName || !qrDataUrl) {
    return;
  }

  const widthMm = 30;
  const heightMm = 15;
  const marginMm = 1;
  const dpi = 300;
  const widthPx = Math.round((widthMm / 25.4) * dpi);
  const heightPx = Math.round((heightMm / 25.4) * dpi);
  const mm = (value: number) => (value / 25.4) * dpi;

  await document.fonts.load(`700 ${Math.round(mm(5))}px "Afacad Flux"`);
  await document.fonts.ready.catch(() => undefined);

  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  ctx.clearRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";

  const margin = mm(marginMm);
  const qrSize = Math.round(heightPx - margin * 2);
  const qrX = widthPx - margin - qrSize;
  const qrY = margin;
  const qrImage = await loadImage(qrDataUrl);
  ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

  const textX = margin;
  const textMaxWidth = qrX - margin - textX;
  const textTop = margin;
  const textBottom = heightPx - margin;
  const textAreaHeight = textBottom - textTop;
  const lines = [givenName, familyName].filter(Boolean);

  let fontSize = Math.round(mm(5.5));
  const minFontSize = Math.round(mm(2.2));
  let lineHeight = fontSize * 1.05;
  while (fontSize > minFontSize) {
    ctx.font = `700 ${fontSize}px "Afacad Flux", sans-serif`;
    lineHeight = fontSize * 1.05;
    const widest = Math.max(
      ...lines.map((line) => ctx.measureText(line).width),
      0,
    );
    const blockHeight = lines.length * lineHeight;
    if (widest <= textMaxWidth && blockHeight <= textAreaHeight) {
      break;
    }
    fontSize -= 1;
  }
  ctx.font = `700 ${fontSize}px "Afacad Flux", sans-serif`;
  lineHeight = fontSize * 1.05;
  const nameBlockHeight = lines.length * lineHeight;
  const nameStartY =
    textTop + (textAreaHeight - nameBlockHeight) / 2 + lineHeight / 2;
  for (let index = 0; index < lines.length; index += 1) {
    ctx.fillText(
      lines[index],
      textX,
      nameStartY + index * lineHeight,
      textMaxWidth,
    );
  }

  printLabelImages([
    {
      dataUrl: canvasToPngDataUrl(canvas),
      title: "30x15+QR",
      widthMm,
      heightMm,
    },
  ]);
}

export function getUpsPackageStatus(
  pkg: CardOrderUpsPackageDetails,
): string | null {
  return pkg.trackingStatus?.trim() || pkg.tracking?.status?.trim() || null;
}

export function isUpsPackageDelivered(
  pkg: CardOrderUpsPackageDetails,
): boolean {
  if (pkg.tracking?.delivered) {
    return true;
  }
  return pkg.tracking?.statusType === "D";
}

export function getOrderUpsTracking(
  order: {
    ups?: { tracking?: CardOrderUpsTrackingDetails | null } | null;
  } | null,
): CardOrderUpsTrackingDetails | null {
  return order?.ups?.tracking ?? null;
}

export function isOrderUpsDelivered(
  order: { status?: string } | null,
  tracking?: {
    delivered?: boolean;
    statusType?: "D" | "I" | "M" | "P" | "X" | null;
  } | null,
): boolean {
  if (order?.status === "DELIVERED") {
    return true;
  }
  if (tracking?.delivered) {
    return true;
  }
  return tracking?.statusType === "D";
}

export function formatUpsTrackingActivityWhen(
  date: string | null,
  time: string | null,
): string | null {
  const normalizedDate = date?.trim();
  if (!normalizedDate) {
    return null;
  }
  if (/^\d{8}$/.test(normalizedDate)) {
    const isoDate = `${normalizedDate.slice(0, 4)}-${normalizedDate.slice(4, 6)}-${normalizedDate.slice(6, 8)}`;
    const normalizedTime = time?.trim();
    if (normalizedTime && /^\d{6}$/.test(normalizedTime)) {
      return `${isoDate} ${normalizedTime.slice(0, 2)}:${normalizedTime.slice(2, 4)}`;
    }
    return isoDate;
  }
  const parts = [normalizedDate, time?.trim()].filter(
    (value): value is string => Boolean(value),
  );
  return parts.length ? parts.join(" ") : null;
}

export function parseUpsPickupDate(
  value: string | null | undefined,
): Date | null {
  const trimmed = value?.trim();
  if (!trimmed || !/^\d{8}$/.test(trimmed)) {
    return null;
  }
  const year = Number(trimmed.slice(0, 4));
  const month = Number(trimmed.slice(4, 6)) - 1;
  const day = Number(trimmed.slice(6, 8));
  const date = new Date(year, month, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export function formatUpsPickupTime(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  if (/^\d{4}$/.test(trimmed)) {
    return `${trimmed.slice(0, 2)}:${trimmed.slice(2, 4)}`;
  }
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [hours, minutes] = trimmed.split(":");
    return `${hours.padStart(2, "0")}:${minutes}`;
  }
  return trimmed;
}

export function formatUpsTrackingActivityLocation(
  city: string | null,
  country: string | null,
): string | null {
  const parts = [city?.trim(), country?.trim()].filter(
    (value): value is string => Boolean(value),
  );
  return parts.length ? parts.join(", ") : null;
}

function upsTrackingActivityTimestamp(
  date: string | null,
  time: string | null,
): number {
  const normalizedDate = date?.trim();
  if (!normalizedDate || !/^\d{8}$/.test(normalizedDate)) {
    return 0;
  }
  const normalizedTime = time?.trim() ?? "000000";
  const paddedTime = normalizedTime.padEnd(6, "0").slice(0, 6);
  const iso = `${normalizedDate.slice(0, 4)}-${normalizedDate.slice(4, 6)}-${normalizedDate.slice(6, 8)}T${paddedTime.slice(0, 2)}:${paddedTime.slice(2, 4)}:${paddedTime.slice(4, 6)}Z`;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sortUpsTrackingActivitiesOldestFirst(
  activities: CardOrderUpsTrackingActivity[],
): CardOrderUpsTrackingActivity[] {
  return [...activities].sort(
    (left, right) =>
      upsTrackingActivityTimestamp(left.date, left.time) -
      upsTrackingActivityTimestamp(right.date, right.time),
  );
}

export function buildCartLineId(
  employeeEmail: string,
  templateId: string,
  pageSizePresetId: string,
): string {
  return `${employeeEmail}:${templateId}:${pageSizePresetId}`;
}

export function employeeToPickerItem(
  employee: Employee,
): EmployeePickerItem | null {
  const email = employee.email?.trim();
  if (!email) {
    return null;
  }
  if (!employee.employeeId) {
    return null;
  }
  return {
    employeeId: employee.employeeId,
    email,
    fullName: employee.fullName,
    givenName: employee.givenName,
    familyName: employee.familyName,
  };
}

function groupCartLinesByEmployee(
  lines: CardOrderCartLine[],
): Map<string, CardOrderCartLine[]> {
  const grouped = new Map<string, CardOrderCartLine[]>();
  for (const line of lines) {
    const email = line.employee.email.trim().toLowerCase();
    const bucket = grouped.get(email) ?? [];
    bucket.push(line);
    grouped.set(email, bucket);
  }
  return grouped;
}

export function groupCartLinesByEmployeeOrdered(
  lines: CardOrderCartLine[],
  employeeOrder: EmployeePickerItem[],
): CardOrderCartEmployeeGroup[] {
  const byEmail = groupCartLinesByEmployee(lines);
  const groups: CardOrderCartEmployeeGroup[] = [];
  const seen = new Set<string>();

  for (const employee of employeeOrder) {
    const key = employee.email.trim().toLowerCase();
    const bucket = byEmail.get(key);
    if (bucket?.length) {
      groups.push({ employee, lines: bucket });
      seen.add(key);
    }
  }

  for (const [key, bucket] of byEmail.entries()) {
    if (!seen.has(key) && bucket.length > 0) {
      groups.push({ employee: bucket[0].employee, lines: bucket });
    }
  }

  return groups;
}

export function getTotalCartQuantity(lines: CardOrderCartLine[]): number {
  return lines.reduce((sum, line) => sum + line.quantity, 0);
}

export function cartLinesToRecipientsPayload(lines: CardOrderCartLine[]) {
  const grouped = groupCartLinesByEmployee(lines);
  return [...grouped.entries()].map(([employeeEmail, employeeLines]) => ({
    employeeEmail,
    items: employeeLines.map((line) => ({
      templateId: line.templateId,
      pageSizePresetId: line.pageSizePresetId,
      quantity: line.quantity,
    })),
  }));
}

export function cartLinesToSelfServiceRecipientsPayload(
  lines: CardOrderCartLine[],
  employeeEmail: string,
) {
  const normalizedEmail = employeeEmail.trim().toLowerCase();
  const ownLines = lines.filter(
    (line) => line.employee.email.trim().toLowerCase() === normalizedEmail,
  );
  return [
    {
      employeeEmail: normalizedEmail,
      items: ownLines.map((line) => ({
        templateId: line.templateId,
        pageSizePresetId: line.pageSizePresetId,
        quantity: line.quantity,
      })),
    },
  ];
}

export function filterOrdersForEmployee(
  orders: Array<{
    placedByEmail?: string | null;
    employeeEmail?: string | null;
    recipients?: Array<{ employeeEmail?: string | null }> | null;
  }>,
  employeeEmail: string,
) {
  const normalizedEmail = employeeEmail.trim().toLowerCase();
  if (!normalizedEmail) {
    return [];
  }
  return orders.filter(
    (order) =>
      order.placedByEmail?.trim().toLowerCase() === normalizedEmail ||
      order.employeeEmail?.trim().toLowerCase() === normalizedEmail ||
      (order.recipients ?? []).some(
        (recipient) =>
          recipient.employeeEmail?.trim().toLowerCase() === normalizedEmail,
      ),
  );
}

export function getOrderRecipientsSummary(order: {
  recipients: Array<{ employeeName: string | null; employeeEmail: string }>;
}): string {
  if (order.recipients.length === 1) {
    const recipient = order.recipients[0];
    return recipient.employeeName || recipient.employeeEmail;
  }
  return String(order.recipients.length);
}

export function getOrderTotalQuantity(order: {
  items: Array<{ quantity: number }>;
}): number {
  return order.items.reduce((sum, item) => sum + item.quantity, 0);
}

function readBranchVariableValue(branch: Branch, name: string): string {
  const variables = branch.variables ?? {};
  const direct = variables[name];
  if (direct?.defaultValue != null) {
    const value = String(direct.defaultValue).trim();
    if (value) {
      return value;
    }
  }

  for (const variable of Object.values(variables)) {
    if (variable?.name !== name || variable.defaultValue == null) {
      continue;
    }
    const value = String(variable.defaultValue).trim();
    if (value) {
      return value;
    }
  }

  return "";
}

export function branchShippingDetails(
  branch: Branch | null,
): BranchShippingDetails | null {
  if (!branch) {
    return null;
  }

  const line1 = readBranchVariableValue(branch, "branchAddress");
  const city = readBranchVariableValue(branch, "branchCity");
  const postalCode = readBranchVariableValue(branch, "branchPostalCode");
  const country = readBranchVariableValue(branch, "branchCountry") || "PL";
  const phone = readBranchVariableValue(branch, "branchPhone");
  const branchName =
    readBranchVariableValue(branch, "branchName") || branch.name.trim();

  if (!line1 || !city || !postalCode) {
    return null;
  }

  return {
    branchName,
    phone: phone || undefined,
    address: {
      line1,
      city,
      postalCode,
      country,
    },
  };
}

export function getDeliverableBranches(
  branches: Branch[],
): DeliverableBranch[] {
  return branches
    .map((branch) => {
      const shipping = branchShippingDetails(branch);
      return shipping ? { branch, shipping } : null;
    })
    .filter((item): item is DeliverableBranch => item !== null);
}

export function getCountryFlagUrl(
  alpha2Code: string | null | undefined,
): string {
  const code = String(alpha2Code ?? "")
    .trim()
    .toUpperCase();
  if (!code) {
    return "";
  }
  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
}

export function formatShippingAddressLines(
  address:
    | {
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        postalCode?: string | null;
      }
    | null
    | undefined,
): string[] {
  if (!address) {
    return [];
  }
  return [
    address.line1,
    address.line2,
    `${address.postalCode ?? ""} ${address.city ?? ""}`.trim(),
  ].filter((line): line is string => Boolean(line?.trim()));
}

export function getVisibleFulfillmentTimelineSteps(
  timeline: CardOrderFulfillmentTimelineStep[] | null | undefined,
): CardOrderFulfillmentTimelineStep[] {
  if (!timeline?.length) {
    return [];
  }
  return timeline.filter(
    (step) =>
      step.state === "completed" ||
      step.state === "current" ||
      step.state === "cancelled",
  );
}

export function getOrderItemsSummary(
  order: {
    items: Array<{ templateName: string; quantity: number }>;
    recipients?: unknown[];
  },
  translate: (key: string, params?: Record<string, unknown>) => string,
): string {
  const quantityUnit = translate("printCardOrders.quantityUnit");
  const totalQuantity = getOrderTotalQuantity(order);
  const recipientCount = order.recipients?.length ?? 1;

  if (recipientCount > 1) {
    const recipientsLabel = translate("printCardOrders.groupRecipientsCount", {
      count: recipientCount,
    });
    return `${recipientsLabel} · ${totalQuantity} ${quantityUnit}`;
  }

  if (order.items.length === 1) {
    return `${order.items[0].templateName} · ${totalQuantity} ${quantityUnit}`;
  }

  const positions = translate("printCardOrders.positionsCount", {
    count: order.items.length,
  });
  return `${positions} · ${totalQuantity} ${quantityUnit}`;
}

export function getOrderItemSizeLabel(
  item: {
    pageSizePresetId: string;
    pageSizeLabelKey?: string;
  },
  translate: (key: string) => string,
): string {
  const labelKey = item.pageSizeLabelKey?.startsWith("printCards.")
    ? item.pageSizeLabelKey
    : PAGE_SIZE_PRESET_LABEL_KEYS[item.pageSizePresetId];
  if (labelKey) {
    const translated = translate(labelKey);
    if (translated && translated !== labelKey) {
      return translated;
    }
  }
  return item.pageSizePresetId;
}

export function buildOrderItemsGroups(
  recipients: OrderItemsListRecipient[],
): OrderItemsGroupView[] {
  let index = 0;

  return recipients.map((recipient) => {
    const rows = recipient.items.map((item) => ({
      index: ++index,
      item,
    }));

    return {
      key: recipient.employeeEmail,
      employeeName: recipient.employeeName?.trim() || recipient.employeeEmail,
      employeeEmail: recipient.employeeEmail,
      fulfillmentBlockedReason: recipient.fulfillmentBlockedReason ?? null,
      lineCount: recipient.items.length,
      totalQuantity: recipient.items.reduce(
        (sum, item) => sum + item.quantity,
        0,
      ),
      rows,
    };
  });
}

export function getOrderItemsListSummary(
  recipients: OrderItemsListRecipient[],
): OrderItemsSummary {
  return {
    recipientCount: recipients.length,
    lineCount: recipients.reduce(
      (sum, recipient) => sum + recipient.items.length,
      0,
    ),
    totalQuantity: recipients.reduce(
      (sum, recipient) =>
        sum +
        recipient.items.reduce((lineSum, item) => lineSum + item.quantity, 0),
      0,
    ),
  };
}

const PAGE_SIZE_PRESET_LABEL_KEYS: Record<string, string> = {
  "paper-cee-russia": "printCards.pageSizePaperCeeRussia",
  "paper-euro-west": "printCards.pageSizePaperEuroWest",
  "paper-90x55": "printCards.pageSizePaper90x55",
  "paper-91x55": "printCards.pageSizePaper91x55",
  "paper-90x54": "printCards.pageSizePaper90x54",
  "paper-89x51-canva": "printCards.pageSizePaper89x51Canva",
  "plastic-85x55": "printCards.pageSizePlastic85x54",
};

export const UPS_SANDBOX_TEST_SHIPPING_ADDRESSES: CardOrderTestShippingAddress[] =
  [
    {
      id: "neuss-de",
      labelKey: "cardOrders.testAddresses.neussDe",
      line1: "Hafenstraße 12",
      city: "Neuss",
      postalCode: "41460",
      country: "DE",
    },
    {
      id: "atlanta-us",
      labelKey: "cardOrders.testAddresses.atlantaUs",
      line1: "123 Fork Rd",
      city: "Atlanta",
      postalCode: "30328",
      country: "US",
    },
    {
      id: "toronto-ca",
      labelKey: "cardOrders.testAddresses.torontoCa",
      line1: "100 Queen St W",
      city: "Toronto",
      postalCode: "M5H 2N2",
      country: "CA",
    },
    {
      id: "mexico-city-mx",
      labelKey: "cardOrders.testAddresses.mexicoCityMx",
      line1: "Av. Paseo de la Reforma 222",
      city: "Mexico City",
      postalCode: "06600",
      country: "MX",
    },
  ];
